/**
 * The two-sided ladder across every venue (docs/market-ux.md §3): one column per side, each row
 * a price level (price · total size · venue dots), cheapest first. Tapping a level opens its
 * per-venue breakdown; tapping a venue there opens the ticket at that venue with the level's price
 * as the max price. Under each column: best price · venue · the gap to fair (consensus).
 *
 * Data: GET /depth/:position_hash per side, refetched about every 10 s (gated by depth:read).
 * Without the scope: the lock with Unlock, plus each venue's best price from liquidity.
 */
import { useMemo, useState } from "react";
import type { LiquidityPosition, PartnerLiquidity } from "@greenbook/client";
import { depthLooksFlipped, gapToFair, ladderLevels, venueQuotes, type LadderLevel, type MarketSide } from "../../lib/marketView.js";
import { fmtCentDelta, fmtPrice, fmtSize } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import { SCOPES, type Access } from "../../lib/omauth.js";
import { venueColorMap } from "../../lib/venueColors.js";
import { Pill, Skeleton } from "../kit.js";
import { ErrorState } from "../ui.js";
import { LockedInline, scopeLabel } from "../Gate.js";
import { VenueDot } from "../DepthPanel.js";
import type { MarketSource } from "./source.js";
import { BlockCard, type MarketCtx } from "./MarketScreen.js";

const TOP_LEVELS = 10;

/** Signed out → sign in; signed in without the scope → Unlock. */
export function ScopeLock({ source, access, scope }: { source: MarketSource; access: Access; scope: string }) {
  if (access.ok) return null;
  if (access.reason === "signed_out") return <LockedInline scope={scope} />;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 px-3 py-3 text-center text-[12px] text-faint">
      <span>Unlock {scopeLabel(scope)} to see this.</span>
      <button
        type="button"
        onClick={() => source.unlock(scope)}
        className="min-h-11 rounded-full border border-amber/60 bg-amber/15 px-3 text-[12px] font-semibold text-amber hover:bg-amber/25 lg:min-h-8"
      >
        Unlock
      </button>
    </div>
  );
}

/** One stable colour per venue for this market (the chart lines use the same map). */
export function useVenueColors(ctx: MarketCtx): Map<string, string> {
  return useMemo(() => {
    const names = ctx.book.positions.flatMap((p) => (p.partner_liquidities ?? []).map((pl) => pl.partner_id));
    return venueColorMap(names);
  }, [ctx.book.positions]);
}

export function OrderBookBlock({ ctx }: { ctx: MarketCtx }) {
  const access = ctx.source.useAccess(SCOPES.depth);
  const colors = useVenueColors(ctx);
  // The venue pills: every venue in scope quoting either side.
  const venues = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of ctx.live) for (const q of venueQuotes(p, ctx.allowed)) m.set(q.id, q.name);
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [ctx.live, ctx.allowed]);
  const [only, setOnly] = useState<Set<string> | null>(null);
  const toggle = (id: string) =>
    setOnly((cur) => {
      const next = new Set(cur ?? []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next.size === 0 ? null : next;
    });

  return (
    <BlockCard title="Order book" right={ctx.allowed ? <span className="text-[11px] text-faint">connected venues</span> : undefined}>
      {venues.length > 1 && (
        <div className="-mx-3 flex min-w-0 items-center gap-1.5 overflow-x-auto px-3" role="toolbar" aria-label="Venues">
          <Pill active={only == null} onClick={() => setOnly(null)}>
            All
          </Pill>
          {venues.map((v) => (
            <Pill key={v.id} active={!!only?.has(v.id)} onClick={() => toggle(v.id)} icon={<VenueDot color={colors.get(v.id)} size={7} />}>
              {v.name}
            </Pill>
          ))}
        </div>
      )}
      {ctx.allowed && ctx.allowed.size === 0 && <p className="text-[12px] text-amber">Real mode: no connected venues, so nothing to show.</p>}
      {!access.ok && <ScopeLock source={ctx.source} access={access} scope={SCOPES.depth} />}
      <div className="grid grid-cols-2 gap-2">
        {ctx.sides.map((s, i) => (
          <SideColumn key={s.positionHash} ctx={ctx} side={s} live={ctx.live[i]} only={only} colors={colors} locked={!access.ok} />
        ))}
      </div>
    </BlockCard>
  );
}

function SideColumn({
  ctx,
  side,
  live,
  only,
  colors,
  locked,
}: {
  ctx: MarketCtx;
  side: MarketSide;
  live: LiquidityPosition | undefined;
  only: Set<string> | null;
  colors: Map<string, string>;
  locked: boolean;
}) {
  const { format } = useOddsFormat();
  const depth = ctx.source.useDepth(locked ? undefined : side.positionHash);
  const quotes = useMemo(() => venueQuotes(live ?? (side.position as LiquidityPosition), ctx.allowed).filter((q) => !only || only.has(q.id)), [live, side.position, ctx.allowed, only]);
  const levels = useMemo(() => ladderLevels(depth.data?.partners ?? [], ctx.allowed, only), [depth.data, ctx.allowed, only]);
  const [open, setOpen] = useState<number | null>(null);
  const [all, setAll] = useState(false);
  const liqBest = quotes[0]?.price ?? null;
  const flipped = depthLooksFlipped(levels[0]?.price ?? null, liqBest);
  const best = quotes[0] ?? null;
  const gap = gapToFair(best?.price ?? null, side.consensus);
  const maxCum = levels.at(-1)?.cum ?? 1;
  const shown = all ? levels : levels.slice(0, TOP_LEVELS);

  /** The ticket at one venue: its liquidity row when it has one (carries the liquidity_hash). */
  const trade = (venueId: string, venueName: string, price: number) => {
    const pos = (live ?? side.position) as LiquidityPosition;
    const pl = (pos.partner_liquidities ?? []).find((x) => x.partner_id === venueId) ?? ({ partner_id: venueId, partner_name: venueName, price } as PartnerLiquidity);
    ctx.openTicket({ position: pos, pl: pl as PartnerLiquidity, maxPrice: price });
  };

  const fallback = (
    <ul className="flex flex-col divide-y divide-border/50 overflow-hidden rounded-md border border-border/70 bg-bg">
      {quotes.length === 0 ? (
        <li className="px-2 py-3 text-center text-[12px] text-faint">No prices</li>
      ) : (
        quotes.map((q) => (
          <li key={q.id}>
            <button type="button" onClick={() => trade(q.id, q.name, q.price)} className="flex min-h-11 w-full items-center gap-1.5 px-2 text-left text-[12px] hover:bg-raised lg:min-h-8">
              <VenueDot color={colors.get(q.id)} size={7} />
              <span className="min-w-0 flex-1 truncate text-dim">{q.name}</span>
              <span className="nums text-ink">{fmtPrice(q.price, q.american, format)}</span>
            </button>
          </li>
        ))
      )}
    </ul>
  );

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex min-w-0 items-baseline gap-1.5">
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink" title={side.position.title ?? side.label}>
          {side.label}
        </span>
        {flipped && (
          <span className="shrink-0 rounded border border-down/60 bg-down/15 px-1 text-[9px] uppercase text-down" title="The depth feed returned the other side's book (FRICTION.md). Prices below are from liquidity.">
            wrong book
          </span>
        )}
      </div>

      {locked || flipped ? (
        fallback
      ) : depth.isError ? (
        <ErrorState error={depth.error} onRetry={depth.refetch} />
      ) : depth.isLoading ? (
        <div className="flex flex-col gap-1">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-7 w-full" />
          ))}
        </div>
      ) : levels.length === 0 ? (
        fallback
      ) : (
        <div className="overflow-hidden rounded-md border border-border/70 bg-bg">
          <div className="grid grid-cols-[minmax(0,1fr)_auto_2.25rem] gap-1 border-b border-border/70 px-2 py-1 text-[10px] uppercase tracking-wide text-faint">
            <span>Price</span>
            <span className="text-right">Size</span>
            <span />
          </div>
          <ul className="flex flex-col">
            {shown.map((l, i) => (
              <Level key={l.price} level={l} best={i === 0} maxCum={maxCum} colors={colors} open={open === l.price} onToggle={() => setOpen(open === l.price ? null : l.price)} onTrade={trade} />
            ))}
          </ul>
          {levels.length > TOP_LEVELS && (
            <button type="button" onClick={() => setAll((v) => !v)} className="min-h-11 w-full border-t border-border/70 text-[12px] text-dim hover:text-amber lg:min-h-8">
              {all ? "Fewer levels" : `+${levels.length - TOP_LEVELS} levels`}
            </button>
          )}
        </div>
      )}

      {/* best · venue · gap to fair */}
      <div className="nums flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-[11px] text-faint">
        <span className="text-[13px] font-semibold text-ink">{best ? fmtPrice(best.price, best.american, format) : "—"}</span>
        {best && <span className="min-w-0 truncate">{best.name}</span>}
        {gap != null && (
          <span title={`Fair (consensus) ${fmtPrice(side.consensus, null, format)}`} className={Math.abs(gap) < 0.5 ? "" : gap > 0 ? "text-dim" : "text-up"}>
            {fmtCentDelta(gap)} vs fair
          </span>
        )}
      </div>
    </div>
  );
}

function Level({
  level,
  best,
  maxCum,
  colors,
  open,
  onToggle,
  onTrade,
}: {
  level: LadderLevel;
  best: boolean;
  maxCum: number;
  colors: Map<string, string>;
  open: boolean;
  onToggle: () => void;
  onTrade: (venueId: string, venueName: string, price: number) => void;
}) {
  const { format } = useOddsFormat();
  const breakdown = level.venues.map((v) => `${v.name} ${fmtSize(v.size)}`).join(" · ");
  return (
    <li className="border-b border-border/40 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        title={breakdown}
        className="relative grid min-h-11 w-full grid-cols-[minmax(0,1fr)_auto_2.25rem] items-center gap-1 px-2 text-left text-[13px] hover:bg-raised/70 lg:min-h-7 lg:text-[12px]"
      >
        <span className="absolute inset-y-0 left-0 bg-amber/10" style={{ width: `${(level.cum / maxCum) * 100}%` }} aria-hidden />
        <span className={`nums relative ${best ? "font-semibold text-amber" : "text-ink"}`}>{fmtPrice(level.price, null, format)}</span>
        <span className="nums relative text-right text-dim">{fmtSize(level.size)}</span>
        <span className="relative flex items-center justify-end gap-0.5">
          {level.venues.slice(0, 3).map((v) => (
            <VenueDot key={v.id} color={colors.get(v.id)} size={6} />
          ))}
          {level.venues.length > 3 && <span className="text-[9px] text-faint">+{level.venues.length - 3}</span>}
        </span>
      </button>
      {open && (
        <ul className="gb-fade flex flex-col bg-panel/60 py-0.5">
          {level.venues.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                onClick={() => onTrade(v.id, v.name, level.price)}
                className="flex min-h-11 w-full items-center gap-1.5 px-2 text-left text-[12px] hover:bg-raised lg:min-h-7"
                title={`Trade ${v.name} at up to ${fmtPrice(level.price, null, format)}`}
              >
                <VenueDot color={colors.get(v.id)} size={7} />
                <span className="min-w-0 flex-1 truncate text-dim">{v.name}</span>
                <span className="nums text-ink">{fmtSize(v.size)}</span>
                <span className="text-[11px] text-amber">Trade ›</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
